import React, { useCallback, useEffect, useMemo, useState } from "react";

import { css } from "@emotion/react";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import {
  GuidedSellingResult,
  NotFinalGuidedSellingStep,
  GuidedSellingTag,
  GuidedSellingStepChoice,
  GuidedSellingResultDto,
  SwarovskiOptikLocaleFrontend,
  type GuidedSellingTranslation,
} from "@swo/storefront";
import { GuidedSellingFinalStepScreen } from "./GuidedSellingFinalStep";
import { StepNavigationBar } from "./StepNavigationBar";
import { GuidedSellingExternalState, GuidedSellingExternalState_Choices, useGuidedSellingState } from "../state/state";
import { GuidedSellingStepChoices } from "./GuidedSellingStepChoices";
import { FONTS } from "../../../styles/fonts";
import { StepHint } from "./GuidedSellingStepHint";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { BREAKPOINTS, CSS_MQ } from "../../../styles/breakpoints";
import cn from "classnames";
import { StepNavigationBarWrapper } from "./StepNavigationBarWrapper";
import { ResultSelectionPicker } from "./ResultSelectionPicker";
import { NonEmptyArray } from "fp-ts/es6/NonEmptyArray";
import { StepProgress } from "./StepProgress";
import { GSBackgroundBlurredImage } from "./GSBackgroundBlurredImage";
import { GSBackgroundImage } from "./GSBackgroundImage";
import { getHintText } from "../state/get-hint-text";
import { pipe } from "fp-ts/lib/function";
import { option } from "fp-ts";
import { useGuidedSellingAnalytics } from "../../../domain/google-tag-manager/guided-selling-tracking-aspect";

export const GuidedSellingStep: ReactTFC<{
  initialStep: NotFinalGuidedSellingStep;
  fetchGSResult: (
    selectedTags: NonEmptyArray<GuidedSellingTag>,
  ) => Promise<GuidedSellingResult<GuidedSellingResultDto>>;
  localeForNumberFormat: SwarovskiOptikLocaleFrontend;
  translations: GuidedSellingTranslation;
  variant: "full-width" | "content-width";
}> = props => {
  const { state, actions } = useGuidedSellingState(props.initialStep, props.fetchGSResult);

  useGuidedSellingAnalytics(state);

  const color: "white" | "black" = !!state.data.currentStep.background?.image ? "white" : "black";
  const colorClassName = `color-${color}`;

  const selectedChoiceId = state.tag === "choices" ? state.data.selected : undefined;
  const onNextOpt = selectedChoiceId ? () => actions.onClickNext(selectedChoiceId) : undefined;

  function getBackground(stateChoices: GuidedSellingExternalState_Choices) {
    return (
      pipe(
        stateChoices.data.currentStep.choices.find(c => c.id === stateChoices.data.selected),
        option.fromNullable,
        option.chain(c => option.fromNullable(c.background)),
        option.alt(() => {
          return option.some(state.data.currentStep.background);
        }),
        option.toUndefined,
      ) || undefined
    );
  }

  const [hoveredChoice, setHoveredChoice] = useState<GuidedSellingStepChoice | undefined>(undefined);

  const onChoiceHovered = useCallback((c: GuidedSellingStepChoice) => {
    setHoveredChoice(c);
  }, []);

  return (
    <div css={stepStyles} className={cn(colorClassName, props.variant, { "step-final": state.tag === "results" })}>
      <div
        css={stepContentStyles}
        className={cn({
          "with-bar":
            state.tag === "choices" ||
            (state.tag === "results" &&
              state.data.results.__tag === "loaded" &&
              state.data.results.data.resultsCount > 1),
        })}
      >
        <div className={cn({ "with-shadow": state.tag === "choices" })} css={internalWrapperStyles}>
          {state.tag === "choices" && <StepProgress progress={state.progress} />}

          {state.tag === "results" && (
            <GuidedSellingFinalStepScreen
              currentStep={state.data.currentStep}
              result={state.data.results}
              onRestart={actions.onClickRestart}
              localeForNumberFormat={props.localeForNumberFormat}
              translations={props.translations}
              onIndexRequested={actions.onIndexRequested}
            />
          )}

          {state.tag === "choices" && (
            <>
              <h3
                className={"u-uppercase"}
                css={css`
                  ${headlineStyles};

                  margin-top: 1.25em;

                  ${CSS_MQ.atLeastMd} {
                    max-width: 100% !important;
                  }
                  ${CSS_MQ.atLeastLg} {
                    max-width: 60% !important;
                  }
                `}
              >
                {state.data.currentStep.headline}
              </h3>
              <div
                css={wrapperStyles}
                className={cn("no-margin", {
                  "choice-diagram": state.data.currentStep.visualLayout === "Rising Curve",
                  "choice-visual-grid": state.data.currentStep.visualLayout === "Visual Grid",
                  "choice-image": state.data.currentStep.visualLayout === "Image Choice",
                })}
              >
                <form
                  onSubmit={e => {
                    e.preventDefault();

                    console.log(e);
                  }}
                  css={sharedStyles}
                >
                  <GuidedSellingStepChoices
                    currentStep={state.data.currentStep}
                    onMakeChoice={actions.onSelectChoice}
                    currentChoice={selectedChoiceId}
                    onChoiceHovered={onChoiceHovered}
                  />
                </form>
              </div>

              <StepHint text={getHintText(state)} translations={props.translations} />
              <GSBackgroundImage
                key={getBackground(state)?.image.url}
                background={getBackground(state)}
                className={cn("_image", {
                  "with-filter":
                    state.data.currentStep.visualLayout === "Rising Curve" ||
                    state.data.currentStep.visualLayout === "Vertical Line",
                })}
              />
              {hoveredChoice &&
                hoveredChoice.background &&
                hoveredChoice.background.image.url !== getBackground(state)?.image.url && (
                  <GSBackgroundImage
                    key={hoveredChoice.background.image.url}
                    background={hoveredChoice.background}
                    className={cn("_image hidden", {
                      "with-filter":
                        state.data.currentStep.visualLayout === "Rising Curve" ||
                        state.data.currentStep.visualLayout === "Vertical Line",
                    })}
                  />
                )}
            </>
          )}
        </div>

        {state.tag === "choices" && (
          <>
            {props.variant === "full-width" && (
              <>
                <GSBackgroundBlurredImage key={getBackground(state)?.image.url} background={getBackground(state)} />
                {state.nextBackgrounds.map(b => {
                  if (state.data.currentStep.background?.image.url === b.image.url) {
                    return null;
                  }

                  return <GSBackgroundBlurredImage key={b.image.url} background={b} className={"_image hidden"} />;
                })}
              </>
            )}

            <StepNavigationBar
              onClickRestart={actions.onClickRestart}
              onClickPrevious={actions.onClickPrevious}
              onClickNext={onNextOpt}
              translations={props.translations}
              firstSelection={state.progress.madeSelectionsCount === 1 ? state.data.selected : undefined}
            />
          </>
        )}

        {state.tag === "results" &&
          state.data.results.__tag === "loaded" &&
          state.data.results.data.resultsCount > 1 && (
            <StepNavigationBarWrapper>
              <ResultSelectionPicker
                currentIndex={state.data.results.data.selectedIndex}
                count={state.data.results.data.resultsCount}
                onIndexRequested={actions.onIndexRequested}
              />
            </StepNavigationBarWrapper>
          )}
      </div>
    </div>
  );
};

export const overflowStyles = css`
  overflow-y: scroll;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  scrollbar-width: none; // firefox

  &::-webkit-scrollbar-thumb {
    background: white;
  }
`;

export const overflowStylesGreen = css`
  overflow-y: scroll;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${CSS_COLORS.Green};
  }
`;

export const containerStyles = css`
  ${CSS_MQ.atLeastMd} {
    width: 100%;
    max-width: var(--size);

    margin-left: auto;
    margin-right: auto;
  }
`;

export const headlineStyles = css`
  ${containerStyles};

  color: var(--color);
  line-height: 1.1;

  ${FONTS.MinervaModern500};

  font-size: 24px;
  letter-spacing: 1.5px;

  ${CSS_MQ.atLeastMd} {
    font-size: 30px;
    letter-spacing: 1.5px;
  }

  ${CSS_MQ.atLeastLg} {
    font-size: 40px;
    letter-spacing: 2px;
  }

  ${CSS_MQ.lowResNotebook} {
    font-size: 24px;
    letter-spacing: 1.5px;
  }

  text-align: center;

  align-self: center;
  z-index: 3;
`;

const internalWrapperStyles = css`
  position: relative;
  flex-grow: 1;

  flex-direction: column;
  width: var(--max-content-width-new);
  align-self: center;

  > *:not(._image) {
    z-index: 3;

    justify-self: center;
    &._left {
      justify-self: center;
    }
  }

  .step-final & {
    grid-template-rows: auto auto 1fr;
  }

  --hint-height-total: 90px;
  ${CSS_MQ.atLeastMd} {
    --hint-height-total: 120px;
  }

  ${CSS_MQ.lowResNotebook} {
    --hint-height-total: 100px;
  }

  ${CSS_MQ.lowResNotebook} {
    // just placeholder - in this case we want to position it absolutely to save space
    --hint-height-total: 40px;
  }

  display: grid;
  grid-template-rows: auto 1fr var(--hint-height-total);

  height: calc(100% - var(--bar-height));

  &.with-shadow {
    --shadow-height: 80px;
    --alpha: 0.25;

    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: calc(-1 * var(--content-gutter));
      width: calc(100% + 2 * var(--content-gutter));

      height: var(--shadow-height);

      z-index: 3;

      background: linear-gradient(180deg, rgba(0, 0, 0, var(--alpha)), rgba(0, 0, 0, 0));
    }
  }
`;

const stepStyles = css`
  position: relative;
  display: flex;

  width: 100%;

  height: calc(100vh - var(--header-unfixed-height));

  @media only screen and (min-width: ${BREAKPOINTS.atLeastSm}px) and (min-height: 840px) {
    height: 700px;
  }

  ${CSS_MQ.lowResNotebook} {
    height: calc(100vh - var(--header-pinned-height));
  }

  &.color-white {
    --color: ${CSS_COLORS.White};
    --anti-color: ${CSS_COLORS.Green};
  }

  &.color-black {
    --color: ${CSS_COLORS.Green};
    --anti-color: ${CSS_COLORS.White};
  }

  --bar-height: 48px;
  --container-gutter-size: 20px;
  --size: 36rem;

  z-index: 99; // over floating segment selector

  background: white;
  &.step-final {
    background-color: ${CSS_COLORS.GreenWhite};
    --color: ${CSS_COLORS.Green};
    --anti-color: ${CSS_COLORS.White};
    --size: 50rem;
  }

  .hidden {
    opacity: 0;
  }
`;

const stepContentStyles = css`
  position: absolute;
  z-index: 1;
  width: 100%;

  overflow: hidden;

  height: 100%;

  display: flex;
  flex-direction: column;
`;

export const wrapperStyles = css`
  ${containerStyles};
  flex-grow: 1;

  display: flex;
  flex-direction: column;
  justify-content: center;

  ${overflowStyles};
  overflow-x: hidden;

  align-self: center;

  width: 100%;
  max-width: 400px;

  .step-final & {
    height: 100%;
    width: 100%;

    &.loading {
      grid-row: 2 / span 2;
    }
  }

  &.no-margin {
    margin-left: 0;
    margin-right: 0;
  }

  &.choice-diagram {
    overflow: hidden;
    height: 100%;

    max-width: 100%;
    ${CSS_MQ.atLeastMd} {
      max-width: 600px;
    }
  }

  &.choice-visual-grid {
    max-width: 100%;
    width: calc(100% - var(--container-gutter-size));
  }

  &.choice-image {
    height: 100%;
  }
`;

export const sharedStyles = css`
  display: grid;
  grid-gap: 5px;

  ${CSS_MQ.atLeastMd} {
    grid-gap: 10px;
  }
  margin-left: -10px;
  margin-right: -10px;

  > * {
    margin-left: 10px;
    margin-right: 10px;
  }

  .choice-diagram & {
    display: flex;
    height: 100%;
    width: 100%;

    margin-left: 0;
    margin-right: 0;

    > * {
      margin-left: 0;
      margin-right: 0;
    }
  }

  .choice-visual-grid & {
    ${CSS_MQ.atLeastSm} {
      display: flex;
      gap: 20px;
      justify-content: center;
      flex-wrap: wrap;

      height: 100%;
      width: 100%;

      margin-left: 0;
      margin-right: 0;

      overflow: hidden;

      > * {
        margin-left: 0;
        margin-right: 0;
      }
    }
  }

  .choice-image & {
    flex-grow: 1;
  }
`;
